import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Per-user manual ordering of customers. Activated by the "Custom" sort
 * option in the customer list — the user drags cards around and the new
 * order is persisted here.
 *
 * Stored as an array of customer IDs in the user's preferred order. Customers
 * not in the list fall to the end of the visible grid (newer customers, etc).
 */
const storageKey = (userId: string) => `riskninja:custom-order:${userId}`;

const readOrder = (userId: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeOrder = (userId: string, ids: string[]) => {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(ids));
  } catch {
    // ignore
  }
};

export const useCustomCustomerOrder = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [order, setOrderState] = useState<string[]>([]);

  useEffect(() => {
    if (!userId) {
      setOrderState([]);
      return;
    }
    setOrderState(readOrder(userId));
  }, [userId]);

  const setOrder = useCallback(
    (ids: string[]) => {
      if (!userId) return;
      setOrderState(ids);
      writeOrder(userId, ids);
    },
    [userId]
  );

  /**
   * Move customer `id` to the position currently held by `targetId`, shifting
   * the rest around. Returns the new ordering scoped to `visibleIds` so that
   * unknown customers don't get dragged into the saved list.
   */
  const reorder = useCallback(
    (visibleIds: string[], draggedId: string, targetId: string) => {
      if (draggedId === targetId) return;
      // Seed from current saved order intersected with visibleIds, then append
      // any visible ids missing from saved order (their natural position).
      const seed = order.filter((id) => visibleIds.includes(id));
      for (const v of visibleIds) {
        if (!seed.includes(v)) seed.push(v);
      }
      const fromIdx = seed.indexOf(draggedId);
      const toIdx = seed.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = seed.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      setOrder(next);
    },
    [order, setOrder]
  );

  return { order, setOrder, reorder };
};
